import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import RenderToast from "../../components/RenderToast";
import API from "../../utils/api";
import classes from "./Login.module.css";

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem("rememberLogin") === "1"
  );

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      navigate("/dashboard");
    }
  }, [navigate]);

  useEffect(() => {
    if (rememberMe) {
      const storedEmail = localStorage.getItem("rememberEmail") || "";
      setEmail(storedEmail);
    }
  }, [rememberMe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!email || !email.includes("@")) {
        RenderToast({
          type: "error",
          message: "Please enter a valid email address",
        });
        setLoading(false);
        return;
      }
      if (!password || password.length < 1) {
        RenderToast({ type: "error", message: "Please enter your password" });
        setLoading(false);
        return;
      }

      const response = await API.login(email, password);

      if (response.status === 200 && response.data.success) {
        localStorage.setItem("authToken", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        if (rememberMe) {
          localStorage.setItem("rememberLogin", "1");
          localStorage.setItem("rememberEmail", email);
        } else {
          localStorage.removeItem("rememberLogin");
          localStorage.removeItem("rememberEmail");
        }
        RenderToast({ type: "success", message: "Login successful!" });
        navigate("/dashboard");
      } else {
        RenderToast({
          type: "error",
          message: response.data.error || "Login failed. Please try again.",
        });
      }
    } catch (err) {
      const msg =
        err.message && err.message.includes("Cannot connect to")
          ? "Cannot connect to server. Please verify the API server URL and XAMPP status."
          : err.message ||
            "Network error. Please check your connection and try again.";
      RenderToast({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.loginWrapper}>
      <Helmet>
        <title>Admin Login | TechMatter Dashboard</title>
        <meta
          name="description"
          content="Secure admin login page for TechMatter. Access your dashboard to manage products, categories, and orders efficiently."
        />
        <link rel="canonical" href="https://techmatter.co.uk/tech-login-478" />
      </Helmet>
      <div className={classes.leftColumn}>
        <Link to={"/"}>
          {" "}
          <img
            src="./techmatter_uk_color_logo.png"
            alt="Logo"
            className={classes.logo}
          />{" "}
        </Link>

        <h1 className={classes.title}>Welcome Back</h1>
        <p className={classes.description}>
          Sign in to access your admin dashboard
        </p>
        <button className={classes.backButton} onClick={() => navigate(-1)}>
          &larr; Back
        </button>
      </div>

      <div className={classes.rightColumn}>
        <form className={classes.loginForm} onSubmit={handleSubmit}>
          {/* Mobile Back Button */}
          <button
            className={classes.backButtonMobile}
            type="button"
            onClick={() => navigate(-1)}
          >
            &larr; Back
          </button>

          <h2 className={classes.formTitle}>Admin Login</h2>
          <p className={classes.formSubtitle}>
            Enter your credentials to continue
          </p>

          <label className={classes.label}>Email Address</label>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            className={classes.input}
            autoComplete="email"
          />

          <label className={classes.label}>Password</label>
          <div className={classes.passwordField}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className={classes.input}
              autoComplete="current-password"
            />
            <button
              type="button"
              className={classes.eyeBtn}
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          <div className={classes.rememberRow}>
            <label className={classes.rememberControl}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
          </div>

          <button
            type="submit"
            className={classes.submitButton}
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}
